import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useAuth, getCookie } from "@coremind/identity-core";
import { useNavigate } from "react-router-dom";
import { clientProposalsDirect, ClientProposalsDirectError } from "../api/clientProposalsDirect";
import PricingSection from "../components/PricingSection";
import PlatformExplainerSection from "../components/PlatformExplainerSection";
import ShippedSitesGallery from "../components/ShippedSitesGallery";
// Consolidated 2026-09-06 out of this site's own hand-synced copy into a real shared pack
// (build-tracker item 25's own flagged follow-up) -- @coremind/client-proposals-ui also backs
// proposals's standalone /discover route. `variant="embedded"` matches this page's original
// in-flow rendering (www's own real site header/nav sits above it, no second wordmark needed).
const RemoteDiscoveryFlow = lazy(() => import("@coremind/client-proposals-ui").then((mod) => ({ default: mod.DiscoveryFlow })));
// Item 39: the anonymous flow's own conversation id, so a visitor who signs up mid-conversation
// can be reattached to it on the other side of auth. Written here (the host), not inside
// DiscoveryFlow itself, via its onConversationId callback -- the component doesn't own storage
// decisions, see its own prop doc.
const ANONYMOUS_CONVERSATION_KEY = "cpa_anonymous_conversation_id";
function DiscoveryFlow({ onConversationId, resumeConversationId, }) {
    return (_jsx(Suspense, { fallback: null, children: _jsx(RemoteDiscoveryFlow, { variant: "embedded", onConversationId: onConversationId, resumeConversationId: resumeConversationId }) }));
}
/**
 * The www site is the Offsetmind hub. Signed out, it's the real discovery flow (BS §14's
 * login-boundary split) plus the platform explainer and shipped-sites gallery (item 27). Signed
 * in, it redirects into the personal-OS dashboard (item 40, `/app`) -- this page itself renders
 * nothing for a signed-in visitor beyond a brief spinner while that redirect (and item 39's
 * conversation claim, if one is pending) resolves.
 */
export default function HomePage() {
    const { session, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const claimedRef = useRef(false);
    const [redirectError, setRedirectError] = useState(null);
    // Direct feedback: a trackpad back-swipe (or the browser back button) leaving www's homepage
    // dropped an anonymous conversation with no way back to it. This reuses the same resume
    // capability item 40 built for the signed-in dashboard -- a returning visitor with a pending
    // conversation reopens straight into it instead of restarting at Hero. The lazy initializer
    // reads this once, synchronously, before the first render decides what to show.
    const [pendingConversationId] = useState(() => window.localStorage.getItem(ANONYMOUS_CONVERSATION_KEY) ?? undefined);
    // Real bug found live-testing www.offsetmind.com 2026-09-07: `useAuth()`'s `session` starts
    // `null` before the async Cognito check resolves, so a returning signed-in visitor briefly saw
    // the anonymous discovery flow, then got yanked to the signed-in hub the instant auth
    // resolved -- a jarring flash/redirect, not two real pages. Block on the real `loading` flag
    // instead of assuming `!session` means signed-out. The claim-check effect below reuses this
    // same gating.
    useEffect(() => {
        if (authLoading || !session || claimedRef.current)
            return;
        claimedRef.current = true;
        (async () => {
            const pendingId = window.localStorage.getItem(ANONYMOUS_CONVERSATION_KEY);
            if (pendingId) {
                window.localStorage.removeItem(ANONYMOUS_CONVERSATION_KEY);
                try {
                    await clientProposalsDirect.claimConversation(pendingId, getCookie("idToken"));
                    navigate(`/app/proposals/${pendingId}`, { replace: true });
                    return;
                }
                catch (err) {
                    // Item 39: claim failed (already claimed elsewhere, 404, network) -- fall through to
                    // the ordinary dashboard landing rather than dead-ending here.
                    setRedirectError(err instanceof ClientProposalsDirectError ? err.message : "Couldn't resume your in-progress proposal.");
                }
            }
            // /app is the Overview hub (AppIndexPage) built for item 40 -- this used to point at
            // /app/proposals from before that page existed, back when Proposals was the only module.
            navigate("/app", { replace: true });
        })();
    }, [authLoading, session, navigate]);
    if (authLoading || session) {
        return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 20 }, children: [_jsx(CircularProgress, { size: 28 }), redirectError && _jsx(Box, { sx: { color: "text.secondary", fontSize: 13 }, children: redirectError })] }));
    }
    // Checked live in a real browser before assuming this was a gap: LandingLayout's own header
    // already renders a real SIGN IN control when signed out (confirmed in identity-ui/site-
    // template-landing's bundles, then visually in a real screenshot) -- no extra affordance
    // needed here.
    return (_jsxs(Box, { children: [_jsx(DiscoveryFlow, { resumeConversationId: pendingConversationId, onConversationId: (id) => window.localStorage.setItem(ANONYMOUS_CONVERSATION_KEY, id) }), _jsx(PlatformExplainerSection, {}), _jsx(ShippedSitesGallery, {}), _jsx(PricingSection, { linkToFullPage: true })] }));
}
